/**
 * Login Page
 * Admin authentication
 */

import { Form, standardSchemaResolver, useForm, z } from "@redenvelopeorg/ofx-electrum"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../context/AuthContext"

const loginSchema = z.object({
  email: z.email().min(1, { error: "Email is required" }),
  password: z.string().min(1, { error: "Password is required" }),
})

type LoginFormType = z.infer<typeof loginSchema>

export function LoginPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()

  const form = useForm<LoginFormType>({
    resolver: standardSchemaResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  })

  const handleSubmit = async (data: LoginFormType) => {
    form.clearErrors()
    setIsLoading(true)

    try {
      await login(data.email, data.password)
      navigate("/dashboard")
    } catch (error) {
      console.error("Login failed:", error)
      form.setError("root", {
        type: "manual",
        message: error instanceof Error ? error.message : "Authentication failed. Please try again.",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background gradient effect */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background:
            "radial-gradient(circle at 30% 20%, rgba(99, 102, 241, 0.15), transparent 50%), radial-gradient(circle at 70% 80%, rgba(168, 85, 247, 0.15), transparent 50%)",
          animation: "gradientShift 10s ease-in-out infinite",
        }}
      />

      {/* Floating blur effects */}
      <div
        style={{
          position: "fixed",
          top: "-200px",
          right: "-200px",
          width: "600px",
          height: "600px",
          borderRadius: "50%",
          filter: "blur(120px)",
          opacity: 0.3,
          background: "radial-gradient(circle, rgba(139, 92, 246, 0.4), transparent 70%)",
          animation: "float 15s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "fixed",
          bottom: "-200px",
          left: "-200px",
          width: "600px",
          height: "600px",
          borderRadius: "50%",
          filter: "blur(120px)",
          opacity: 0.3,
          background: "radial-gradient(circle, rgba(59, 130, 246, 0.4), transparent 70%)",
          animation: "float 20s ease-in-out infinite reverse",
          pointerEvents: "none",
        }}
      />

      {/* Main container */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          width: "100%",
          maxWidth: "440px",
          padding: "20px",
        }}
      >
        {/* Glassmorphism card */}
        <div
          style={{
            background: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(40px)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "24px",
            padding: "48px 40px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
          }}
        >
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: "40px" }}>
            <div
              style={{
                width: "64px",
                height: "64px",
                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)",
                borderRadius: "16px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "24px",
                boxShadow: "0 0 40px rgba(139, 92, 246, 0.4), 0 10px 30px rgba(0, 0, 0, 0.3)",
                transition: "transform 0.3s ease",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px) scale(1.05)"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)"
              }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <title>Admin Dashboard Logo</title>
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <h1
              style={{
                fontSize: "32px",
                fontWeight: 700,
                background: "linear-gradient(135deg, #f8fafc 0%, #cbd5e1 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                marginBottom: "8px",
                letterSpacing: "-0.02em",
              }}
            >
              Welcome back
            </h1>
            <p style={{ color: "#94a3b8", fontSize: "15px", fontWeight: 400 }}>
              Sign in to access your admin dashboard
            </p>
          </div>

          {/* Form */}
          <Form form={form} schema={loginSchema} onSubmit={handleSubmit}>
            <div className="flex flex-col gap-4">
              {/* Email field */}
              <div>
                <div
                  style={{
                    position: "relative",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "12px",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)"
                    e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.15)"
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)"
                    e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)"
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)"
                    e.currentTarget.style.borderColor = "rgba(139, 92, 246, 0.5)"
                    e.currentTarget.style.boxShadow = "0 0 0 4px rgba(139, 92, 246, 0.1)"
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)"
                    e.currentTarget.style.boxShadow = "none"
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#64748b",
                      pointerEvents: "none",
                      zIndex: 1,
                    }}
                  >
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <title>Email icon</title>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                      />
                    </svg>
                  </div>
                  <input
                    {...form.register("email")}
                    type="email"
                    placeholder="admin@example.com"
                    autoComplete="email"
                    style={{
                      width: "100%",
                      padding: "16px 48px",
                      background: "transparent",
                      border: "none",
                      color: "#f1f5f9",
                      fontSize: "15px",
                      fontWeight: 500,
                      outline: "none",
                    }}
                  />
                </div>
                {form.formState.errors.email && (
                  <p style={{ color: "#fca5a5", fontSize: "13px", marginTop: "6px", paddingLeft: "4px" }}>
                    {form.formState.errors.email.message}
                  </p>
                )}
              </div>

              {/* Password field */}
              <div>
                <div
                  style={{
                    position: "relative",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "12px",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)"
                    e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.15)"
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)"
                    e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)"
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#64748b",
                      pointerEvents: "none",
                      zIndex: 1,
                    }}
                  >
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <title>Password icon</title>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                  <input
                    {...form.register("password")}
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    style={{
                      width: "100%",
                      padding: "16px 48px",
                      background: "transparent",
                      border: "none",
                      color: "#f1f5f9",
                      fontSize: "15px",
                      fontWeight: 500,
                      outline: "none",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: "absolute",
                      right: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      color: "#64748b",
                      cursor: "pointer",
                      padding: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.3s ease",
                      borderRadius: "8px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "#8b5cf6"
                      e.currentTarget.style.background = "rgba(139, 92, 246, 0.1)"
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "#64748b"
                      e.currentTarget.style.background = "none"
                    }}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <title>Hide password</title>
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                        />
                      </svg>
                    ) : (
                      <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <title>Show password</title>
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {form.formState.errors.password && (
                  <p style={{ color: "#fca5a5", fontSize: "13px", marginTop: "6px", paddingLeft: "4px" }}>
                    {form.formState.errors.password.message}
                  </p>
                )}
              </div>

              {/* Error message */}
              {form.formState.errors.root && (
                <div
                  style={{
                    padding: "12px 16px",
                    background: "rgba(239, 68, 68, 0.1)",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    color: "#fca5a5",
                    borderRadius: "12px",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "start",
                    gap: "10px",
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{ flexShrink: 0, marginTop: "1px" }}
                  >
                    <title>Alert icon</title>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>{form.formState.errors.root.message}</span>
                </div>
              )}

              {/* Submit button */}
              <button
                type="submit"
                disabled={isLoading}
                style={{
                  width: "100%",
                  padding: "16px",
                  marginTop: "12px",
                  background: isLoading
                    ? "rgba(100, 116, 139, 0.5)"
                    : "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)",
                  border: "none",
                  borderRadius: "12px",
                  color: "white",
                  fontSize: "16px",
                  fontWeight: 600,
                  cursor: isLoading ? "not-allowed" : "pointer",
                  transition: "all 0.3s ease",
                  boxShadow: isLoading ? "none" : "0 0 30px rgba(139, 92, 246, 0.3), 0 10px 25px rgba(0, 0, 0, 0.3)",
                  position: "relative",
                  overflow: "hidden",
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.transform = "translateY(-2px)"
                    e.currentTarget.style.boxShadow = "0 0 40px rgba(139, 92, 246, 0.4), 0 15px 35px rgba(0, 0, 0, 0.4)"
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)"
                  e.currentTarget.style.boxShadow = "0 0 30px rgba(139, 92, 246, 0.3), 0 10px 25px rgba(0, 0, 0, 0.3)"
                }}
                onMouseDown={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.transform = "translateY(0)"
                  }
                }}
              >
                {isLoading ? "Signing in..." : "Sign In"}
              </button>
            </div>
          </Form>

          {/* Footer */}
          <div
            style={{
              marginTop: "40px",
              paddingTop: "24px",
              borderTop: "1px solid rgba(255, 255, 255, 0.05)",
              textAlign: "center",
            }}
          >
            <p style={{ color: "#64748b", fontSize: "13px", fontWeight: 400 }}>
              © {new Date().getFullYear()} OpenFX API Sandbox. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>
        {`
					@keyframes gradientShift {
						0%, 100% { opacity: 0.5; transform: scale(1); }
						50% { opacity: 0.8; transform: scale(1.1); }
					}
					@keyframes float {
						0%, 100% { transform: translate(0, 0) rotate(0deg); }
						33% { transform: translate(30px, -30px) rotate(5deg); }
						66% { transform: translate(-20px, 20px) rotate(-5deg); }
					}
					input::placeholder {
						color: #64748b;
						font-weight: 400;
					}
					input:focus::placeholder {
						color: #475569;
					}
				`}
      </style>
    </div>
  )
}
